import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { List, X, WhatsappLogo } from "@phosphor-icons/react";
import { NAV_LINKS, SITE, openWhatsApp } from "@/lib/site";
import { useLang } from "@/lib/i18n";
import { LanguageToggle } from "@/components/LanguageToggle";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLang();
  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const textColor = transparent ? "text-white" : "text-forest";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        transparent
          ? "bg-transparent py-5"
          : "bg-surface/95 py-3 shadow-[0_1px_0_rgba(14,90,55,0.08)] backdrop-blur-md"
      }`}
      data-testid="main-navbar"
    >
      <nav className="container-x flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img
            src={SITE.logo}
            alt="RK Jain"
            className="h-14 w-auto object-contain md:h-16"
          />
        </Link>

        <div className="hidden items-center gap-6 xl:flex">
          {NAV_LINKS.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={`link-underline text-sm font-medium tracking-wide transition-colors ${textColor} ${
                  active ? "after:w-full" : ""
                } hover:text-gold`}
              >
                {t(`nav.${l.key}`)}
              </Link>
            );
          })}
        </div>

        <div className="hidden items-center gap-3 xl:flex">
          <LanguageToggle variant={transparent ? "dark" : "light"} />
          <button
            onClick={() => openWhatsApp()}
            data-testid="nav-enquire-button"
            className="btn-gold px-6 py-3 text-sm"
          >
            <WhatsappLogo weight="fill" size={18} />
            {t("common.enquireNow")}
          </button>
        </div>

        <div className="flex items-center gap-3 xl:hidden">
          <LanguageToggle variant={transparent ? "dark" : "light"} />
          <button
            className={textColor}
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={28} /> : <List size={28} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div
          className="absolute inset-x-0 top-full border-t border-hairline bg-surface px-6 py-6 shadow-lg xl:hidden"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="border-b border-hairline py-3 text-base font-medium text-ink hover:text-forest"
                data-testid={`mobile-nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
              >
                {t(`nav.${l.key}`)}
              </Link>
            ))}
          </div>
          <button
            onClick={() => {
              setOpen(false);
              navigate("/enquiry");
            }}
            className="btn-gold mt-6 w-full justify-center"
            data-testid="mobile-enquire-button"
          >
            {t("common.enquireNow")}
          </button>
        </div>
      )}
    </header>
  );
};

export default Navbar;
